<template>
  <div>
    <div v-if="chartType === 'security'" class="scrollable">
      <h2 class="text-h5 text-center mb-6">
        Verified Security Event Trends
      </h2>
      <LineGraph
        title="Verified Events By Category"
        :chart-data="chartsData.topicsByDate || {}"
        :preselected-legend="topicsSelectedLegend"
        :legend="topicsSortedLegend"
        width="500px"
        tooltip-formatter-name="tooltipFormatterPercentages"
      >
      </LineGraph>
      <VerifiedEventsPieGraph
        title="Verified Events By Sector"
        :chart-data="chartsData.sectorTypes || []"
      >
      </VerifiedEventsPieGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Attack Type"
        :chart-data="chartsData.attackTypes || {}"
        rotation="30"
      >
      </VerifiedEventsBarGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Perpetrator"
        :chart-data="chartsData.perpetratorTypes || {}"
        rotation="30"
      >
      </VerifiedEventsBarGraph>
    </div>

    <div v-if="chartType === 'maritime'" class="scrollable">
      <h2 class="text-h5 text-center mb-6">
        Verified Maritime Event Trends
      </h2>
      <LineGraph
        title="Verified Events By Category"
        :chart-data="chartsData.topicsByDate || {}"
        :preselected-legend="topicsSelectedLegend"
        :legend="topicsSortedLegend"
        width="500px"
        tooltip-formatter-name="tooltipFormatterPercentages"
      >
      </LineGraph>
      <VerifiedEventsPieGraph
        title="Verified Events By Vessel Type"
        :chart-data="chartsData.vesselTypes || []"
      >
      </VerifiedEventsPieGraph>
      <VerifiedEventsBarGraph
        title="Verified Events By Time of Day"
        :chart-data="chartsData.hourOfDay || {}"
        rotation="45"
      >
      </VerifiedEventsBarGraph>
    </div>

    <div
      v-if="
        chartType === 'all' ||
        chartType === 'detected' ||
        chartType === 'hotspots'
      "
      class="scrollable"
    >
      <h2 class="text-h5 text-center mb-6">
        Event Trends
      </h2>

      <LineGraph
        title="Events By Category"
        :chart-data="chartsData.topicsByDate || {}"
        :preselected-legend="topicsSelectedLegend"
        :legend="topicsSortedLegend"
        width="500px"
        tooltip-formatter-name="tooltipFormatterPercentages"
      >
      </LineGraph>

      <LineGraph
        title="Events AI Emotions"
        width="500px"
        data-type="emotions"
        :chart-data="chartsData.emotionsByDate || {}"
        :preselected-legend="emotionsSelectedLegend"
        tooltip-formatter-name="tooltipFormatterPercentages"
        :show-description-icon="true"
        description-type="emotion"
      >
      </LineGraph>

      <SentimentChart
        :histogram="chartsData.sentimentHistogram || []"
        :if-loading="isLoading"
        title="Events AI Sentiment"
        :include-refresh-button="false"
        :mark-line-interval="30"
        :show-description-icon="true"
      />
    </div>
  </div>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";

import { MODULE_PREFIX_SEARCH } from "@/store";
import { mapGetters } from "vuex";
import { getSortedPrimaryTopics } from "@/services/events/topics.service";

export default {
  name: "EventsCharts",
  components: {
    LineGraph: () => import("@/components/events/LineGraph"),
    SentimentChart: () => import("@/components/common/details/SentimentChart"),
    VerifiedEventsPieGraph: () =>
      import("@/components/events/VerifiedEventsPieGraph"),
    VerifiedEventsBarGraph: () =>
      import("@/components/events/VerifiedEventsBarGraph"),
  },
  computed: {
    ...mapGetters({
      details: MODULE_PREFIX_SEARCH + "charts",
    }),
    chartType() {
      return this.details.type;
    },
    chartsData() {
      return this.details.data;
    },
    /**
     * Determines which series in the topic charts should be selected in the legend.
     * @returns {{}}
     */
    topicsSelectedLegend() {
      if (
        !this.chartsData?.topicsByDate ||
        !Object.keys(this.chartsData?.topicsByDate).length
      ) {
        return {};
      }

      let selected = {};

      //Get the topics that are set on the search params
      const topics = this.details.searchParams?.topics || [];

      //Only select the topics from the search, or if there are no topics from the search, select all topics.
      //The total series is always selected
      this.chartsData.topicsByDate.seriesData?.forEach((series) => {
        selected[series.name] =
          topics.length === 0 ||
          topics.includes(series.id) ||
          series.id === "total";
      });

      return selected;
    },
    topicsSortedLegend() {
      if (!this.chartsData?.topicsByDate?.seriesData) {
        return [];
      }

      //Sort the topics the same way the topic filters are sorted
      const allTopics = getSortedPrimaryTopics(this.chartType);
      const chartTopics = this.chartsData.topicsByDate.seriesData.map(
        (s) => s.name
      );
      let filteredTopics = allTopics.filter((t) =>
        chartTopics.includes(t.plural)
      );
      filteredTopics = lodashOrderBy(filteredTopics, ["plural"]);

      return ["Total", ...filteredTopics.map((t) => t.plural)];
    },
    /**
     * Determines which series in the emotions charts should be selected in the legend.
     * @returns {{}}
     */
    emotionsSelectedLegend() {
      if (
        !this.chartsData?.emotionsByDate ||
        !Object.keys(this.chartsData?.emotionsByDate).length
      ) {
        return {};
      }

      let selected = {};

      //Get the emotions that are set on the search params
      const emotions = this.details.searchParams?.emotions || [];

      //Only select the emotions from the search, or if there are no emotions from the search, select all emotions.
      //The total series is always selected
      this.chartsData.emotionsByDate.seriesData?.forEach((series) => {
        selected[series.name] =
          emotions.length === 0 ||
          emotions.includes(series.id) ||
          series.id === "total";
      });

      return selected;
    },
    isLoading() {
      return !Object.keys(this.chartsData)?.length;
    },
  },
};
</script>

<style>
.scrollable {
  height: calc(100vh - 50px); /* 50px for header */
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 60px;
}
.chart-container {
  margin-bottom: 20px;
}
.theme--dark.v-sheet {
  background-color: #000000 !important;
  border-color: #000000 !important;
}
</style>
